import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { SALENG_GREEN_API_PROD, SALENG_SHOP_API_PROD } from "./../config";

const SALENG_GREEN_API = SALENG_GREEN_API_PROD;
const SALENG_SHOP_API = SALENG_SHOP_API_PROD;

/** --------- Helpers: ดึงค่าทุกครั้งที่เรียก --------- */
const getAccessToken = () => localStorage.getItem("accessToken") || null;

const decodeToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const getIds = () => {
  const decoded = decodeToken();
  return {
    shopId: decoded?.shopId ?? null,
    branchId: decoded?.branchId ?? null,
    // ถ้ามีใน token ก็เติมอย่างอื่นได้ เช่น branchCode: decoded?.branchCode ?? null
  };
};

// optional: เรียกตอน logout
export const clearAuth = () => {
  localStorage.removeItem("accessToken");
  // ถ้าเคยตั้ง axios.defaults.headers.Authorization ก็ล้างด้วย
  delete axios.defaults.headers.common.Authorization;
};

/** --------- Auth --------- */
export const loginShop = (payload) =>
  axios.post(`${SALENG_SHOP_API}/auth/login`, payload, {
    headers: { "Content-Type": "application/json" },
  });

/** --------- Categories --------- */
export const getCategories = () =>
  axios.get(`${SALENG_GREEN_API}/categories`, {
    headers: { "Content-Type": "application/json" },
  });

/** --------- Product --------- */
export const getProducts = () => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/${shopId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createProduct = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/products`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteProduct = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/products/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateProduct = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/products/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

/** --------- Shop --------- */
export const getShop = (id) => {
  const { shopId } = getIds();
  const targetId = id ?? shopId;
  if (!targetId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/shop/${targetId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createShop = (payload, _shopId) => {
  const { shopId } = getIds();
  const finalShopId = _shopId ?? shopId;
  if (!finalShopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/shop`,
    { ...payload, shopId: finalShopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

/** --------- Branches --------- */
export const createBranch = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/branches`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteBranch = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/branches/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateBranch = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/branches/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

/** --------- Users --------- */
export const getUser = (id) =>
  axios.get(`${SALENG_GREEN_API}/users/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

export const createUser = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/users`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteUser = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/users/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateUser = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/users/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

/** --------- Transaction --------- */
export const getTransaction = (transactionId) =>
  axios.get(`${SALENG_SHOP_API}/transaction/${transactionId}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getBranchTransaction = () => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transaction/branch/${branchId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

// สร้าง bill number — ตอนนี้ใช้ branchId แทน branchCode ถ้ายังไม่มีรหัสสาขา
const generateBillNumber = (branchCodeOrId, lastNumber) => {
  const today = new Date();
  const datePart = today.toISOString().slice(0, 10).replace(/-/g, ""); // YYYYMMDD
  const newNumber = String(lastNumber + 1).padStart(3, "0");
  const formattedBranchCode = String(branchCodeOrId).padStart(2, "0");
  return `B${formattedBranchCode}-${datePart}-${newNumber}`;
};

export const createTransaction = async (payload) => {
  try {
    const { shopId, branchId } = getIds();
    if (!shopId || !branchId)
      throw new Error("Missing shopId/branchId (token?)");

    const transactionsResponse = await getBranchTransaction();
    const transactions = transactionsResponse?.data?.data ?? [];
    const billNumber = generateBillNumber(branchId, transactions.length); // ถ้ามี branchCode จริง ให้เปลี่ยนมาใช้

    return axios.post(
      `${SALENG_SHOP_API}/transaction`,
      { ...payload, branchId, shopId, billNumber },
      { headers: { "Content-Type": "application/json" } }
    );
  } catch (error) {
    console.error("Error creating transaction:", error);
    return Promise.reject(error);
  }
};

export const subscribeTransaction = (onMessage, onError) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId) throw new Error("Missing shopId/branchId (token)?");

  const url = `${SALENG_SHOP_API}/dashboard/stream?shopId=${shopId}&branchId=${branchId}`;
  const es = new EventSource(url);

  es.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage?.(data);
    } catch (err) {
      console.error("Failed to parse SSE data:", err);
    }
  };

  es.onerror = (err) => {
    console.error("SSE connection error:", err);
    onError?.(err);
  };

  return es;
};

/** --------- Points / Files --------- */
export const getUserByPhone = (phoneNumber) =>
  axios.get(`${SALENG_GREEN_API}/users/by-phone/${phoneNumber}`, {
    headers: { "Content-Type": "application/json" },
  });

export const addPointToUser = (id, body) =>
  axios.post(`${SALENG_GREEN_API}/users/${id}/add-point`, body, {
    headers: { "Content-Type": "application/json" },
  });

export const Upload = (formData) =>
  fetch(`${SALENG_SHOP_API}/files`, {
    method: "POST",
    body: formData,
  });
